import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  cn,
  Badge,
  TableCell,
  Checkbox,
  Button,
  TableRow,
} from '@wove/web-ui'
import { FileText, MoreHorizontal, Trash2, AlertCircle, AlertTriangle } from 'lucide-react'
import { Source, SourceStatus } from '@wove/api/types'
import { exportData, formatFileSize, getStatusIcon } from '@src/util'
import { format } from 'date-fns'
import { useRestApi, SourceType, useDeleteSourceMutation, ExportOption } from '@wove/api'
import { useAuth } from '@src/hooks/useAuth'
import { DownloadCloud } from 'lucide-react'

export function FileRow({
  onFileSelected,
  file,
  isSelected,
}: {
  onFileSelected: (id: string) => void
  file: Source
  isSelected: boolean
}) {
  const [, executeDelete] = useDeleteSourceMutation()
  const restApi = useRestApi()

  const { user, customer } = useAuth()

  if (!user || !customer) {
    return null
  }

  const exportOptions: ExportOption[] = []

  const allExportOptions: ExportOption[] = [
    { label: 'Export data to GPM', format: 'gpm' },
    { label: 'Export data to Winmore (GPM)', format: 'winmore-gpm' },
    { label: 'Export data to Explorate', format: 'explorate' },
  ]

  const exportFormats = customer?.rateIngestion?.exportFormats || []
  const isAllExport = exportFormats.length === 1 && exportFormats[0] === 'all'

  if (isAllExport) {
    exportOptions.push(...allExportOptions)
  } else {
    exportOptions.push(
      ...allExportOptions.filter((option) => exportFormats.includes(option.format)),
    )
  }

  const handleExport = (format: SourceType) => {
    exportData(restApi, file.id, format)
  }

  return (
    <TableRow
      key={file.id}
      className={cn(
        'transition-all duration-500',
        // file.isNew && 'animate-in slide-in-from-top-2 fade-in duration-300 bg-blue-50/50',
        isSelected && 'bg-muted/50',
        file.status === SourceStatus.Failed && 'bg-red-50/30',
        // file.status === 'warning' && 'bg-amber-50/30',
      )}
    >
      <TableCell>
        <Checkbox
          checked={isSelected}
          onChange={() => onFileSelected(file.id)}
          aria-label={`Select ${file.name}`}
        />
      </TableCell>
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          <FileText
            className={cn(
              'h-4 w-4 flex-shrink-0 text-muted-foreground',
              //   file.isNew && 'text-blue-500 animate-pulse',
            )}
          />
          <span className="truncate max-w-[180px] sm:max-w-[250px] md:max-w-[350px]">
            {file.name}
          </span>
          {file.status && file.status !== SourceStatus.Complete && (
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 p-0"
              //   onClick={() => showErrorDetails(file)}
            >
              {file.status === SourceStatus.Failed ? (
                <AlertCircle className="h-4 w-4 text-red-500" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-amber-500" />
              )}
            </Button>
          )}
        </div>
      </TableCell>
      <TableCell></TableCell>
      <TableCell>
        <Badge variant="outline">{file.type}</Badge>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {file.tags.map((tag, i) => (
            <Badge
              key={i}
              variant="secondary"
              className={cn(
                'text-xs transition-all',
                // file.isNew && tag === 'New' && 'bg-blue-100 text-blue-800 animate-pulse',
              )}
            >
              {tag.name}
            </Badge>
          ))}
        </div>
      </TableCell>
      <TableCell>{file.size ? formatFileSize(file.size) : ''}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          {getStatusIcon(file.status)}
          <span>{format(new Date(file.createdAt), 'MMM do, yyyy')}</span>
        </div>
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[200px] sm:w-[250px] md:w-[280px]">
            <DropdownMenuLabel className="text-xs sm:text-sm">Export Options</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => handleExport('original')}
              className="text-xs sm:text-sm"
            >
              <DownloadCloud className="mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span>Export Original</span>
            </DropdownMenuItem>
            {exportOptions.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs sm:text-sm">
                  Export to System
                </DropdownMenuLabel>
                {exportOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.format}
                    onClick={() => handleExport(option.format)}
                    className="text-xs sm:text-sm"
                  >
                    <span>{option.label}</span>
                  </DropdownMenuItem>
                ))}
              </>
            )}
            {/* {file.status === SourceStatus.Failed && (
              <>
                <DropdownMenuItem
                  onClick={() => showErrorDetails(file)}
                  className="text-xs sm:text-sm"
                >
                  <AlertCircle className="mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span>View Issues</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => retryProcessing(file.id)}
                  className="text-xs sm:text-sm"
                >
                  <RefreshCcw className="mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span>Retry Processing</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )} */}
            {/* <DropdownMenuItem
              onClick={() => handleViewDetails(file.id)}
              className="text-xs sm:text-sm"
            >
              <span>View Details</span>
            </DropdownMenuItem> */}
            <DropdownMenuItem
              onClick={() => executeDelete({ deleteSourceId: file.id })}
              className="text-red-600 text-xs sm:text-sm"
            >
              <Trash2 className="mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span>Delete Source</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  )
}
